<template>
    <div class="farm-list-table">
        <el-table
            :data="data.reports"
            :empty-text="translate('no_data')"
            highlight-current-row
        >
            <el-table-column type="expand">
                <template v-slot="props">
                    <p>
                        {{ translate('delivery_location') }}:
                        <strong>{{ props.row.receivingLocation }}</strong>
                    </p>
                </template>
            </el-table-column>
            <el-table-column
                min-width="120px"
                prop="contractNumber"
                :label="translate('contract_number')"
            />
            <el-table-column
                align="right"
                prop="contractVolume"
                :formatter="formatNumber"
                :label="`${translate('transaction_volume')} (t)`"
            />
            <el-table-column
                prop="harvestYear"
                :label="translate('agricultural_campaign')"
                min-width="100px"
            />
            <el-table-column prop="culture" :label="translate('cultivation')" />
            <el-table-column
                min-width="120px"
                prop="contractDate"
                :label="translate('report_delivery_date')"
            >
                <template v-slot="scope">
                    {{
                        formatters.converterDataFullParaDataBR(
                            scope.row.contractDate
                        )
                    }}
                </template>
            </el-table-column>
            <el-table-column prop="clientName" :label="translate('client')" />
            <el-table-column
                min-width="120px"
                prop="status"
                align="center"
                :label="translate('status')"
            >
                <template v-slot="scope">
                    <el-tag
                        :type="statusColors[scope.row.status].color"
                        style="text-wrap: nowrap"
                        size="mini"
                    >
                        {{
                            $t(
                                `contrato_rastreabilidade.messages.status.${
                                    statusColors[scope.row.status].label
                                }`
                            )
                        }}
                    </el-tag>
                </template>
            </el-table-column>
            <el-table-column :label="translate('actions')" align="right">
                <template class="" v-slot:default="scope">
                    <el-dropdown
                        class="action-button"
                        trigger="click"
                        @click="scope"
                    >
                        <span class="el-dropdown-link">
                            <i class="mdi mdi-dots-vertical" />
                        </span>
                        <el-dropdown-menu v-slot="dropdown">
                            <el-dropdown-item
                                icon="el-icon-view"
                                @click.native="handleView(scope.row)"
                            >
                                {{ $t('contrato_rastreabilidade.words.view') }}
                            </el-dropdown-item>
                            <el-dropdown-item
                                v-if="!['REPROVADO'].includes(scope.row.status)"
                                icon="el-icon-edit"
                                @click.native="handleEdit(scope.row)"
                            >
                                {{ $t('contrato_rastreabilidade.words.edit') }}
                            </el-dropdown-item>
                            <el-dropdown-item
                                v-if="
                                    ['NAO_ENVIADO', 'ENVIADO'].includes(
                                        scope.row.status
                                    )
                                "
                                icon="el-icon-delete"
                                @click.native="handleRemove(scope.row)"
                            >
                                {{
                                    $t('contrato_rastreabilidade.words.delete')
                                }}
                            </el-dropdown-item>
                            <el-dropdown-item
                                v-if="
                                    !['NAO_ENVIADO', 'REPROVADO'].includes(
                                        scope.row.status
                                    )
                                "
                                icon="el-icon-document"
                                @click.native="handleViewReport(scope.row)"
                            >
                                {{
                                    $t(
                                        'contrato_rastreabilidade.words.view_report'
                                    )
                                }}
                            </el-dropdown-item>
                            <el-dropdown-item
                                v-if="
                                    ['NAO_ENVIADO'].includes(scope.row.status)
                                "
                                icon="el-icon-s-promotion"
                                @click.native="openSendForApproval(scope.row)"
                            >
                                {{
                                    $t(
                                        'contrato_rastreabilidade.words.send_approve'
                                    )
                                }}
                            </el-dropdown-item>
                        </el-dropdown-menu>
                    </el-dropdown>
                </template>
            </el-table-column>
        </el-table>
        <div class="farm-list-table__pagination">
            <div class="total">
                <span
                    >{{ data.totalElements }}
                    {{
                        $tc(
                            'contrato_rastreabilidade.words.results',
                            data.totalElements
                        )
                    }}</span
                >
                <div style="margin: 0 20px"></div>
                <span
                    >{{
                        $t('contrato_rastreabilidade.words.views_by_page')
                    }}:</span
                >
                <el-pagination
                    @size-change="handleSizeChange"
                    :current-page="data.page"
                    :page-sizes="[10, 20, 30, 40]"
                    :page-size="data.size"
                    layout="sizes"
                    :total="data.totalElements"
                />
            </div>
            <div class="prev_next">
                <el-pagination
                    @current-change="handleCurrentChange"
                    :current-page="data.page"
                    :page-sizes="[10, 20, 30, 40]"
                    :page-size="data.size"
                    layout="prev, pager, next, jumper"
                    :total="data.totalElements"
                />
            </div>
        </div>
        <dialog-contract-finish
            v-if="showDialogContractFinish"
            v-model="showDialogContractFinish"
            @onSent="fetchTraceabilityReports()"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { statusColors } from '@/modules/traceability-contract/utils';
import formatters from '@/utils/formatters';

export default {
    name: 'ReportDefault',
    components: {
        dialogContractFinish: () =>
            import(
                '@/modules/traceability-contract/views/components/dialog-contract-finish/index.vue'
            )
    },
    data() {
        return {
            statusColors: statusColors,
            formatters,
            showDialogContractFinish: false,
            currentContract: null
        };
    },
    computed: {
        ...mapGetters('traceabilityContractModule', [
            'getReportList',
            'isRegistrant',
            'isApprover'
        ]),
        ...mapState('traceabilityContractModule', ['filter']),
        data() {
            return this.getReportList;
        }
    },
    watch: {
        showDialogContractFinish(value) {
            if (!value) {
                this.$store.commit('traceabilityContractModule/RESET_FORM');
            }
        }
    },
    methods: {
        ...mapActions('traceabilityContractModule', [
            'fetchTraceabilityReports',
            'destroyTraceabilityReport',
            'downloadTraceabilityReport',
            'fetchTraceabilityReport'
        ]),
        translate(key, params = {}) {
            const base = 'contrato_rastreabilidade.messages.main_list_table';
            return this.$t(`${base}.${key}`, params);
        },
        handleCurrentChange(page) {
            this.$store.commit('traceabilityContractModule/SET_FILTER', {
                ...this.filter,
                page: page - 1,
                size: this.data.size
            });
        },
        handleSizeChange(size) {
            this.$store.commit('traceabilityContractModule/SET_FILTER', {
                page: this.data.page,
                size: size
            });
        },
        handleView(row) {
            this.$router.push({
                name: 'contrato-rastreabilidade-visualizar',
                params: { id: row.id }
            });
        },
        async handleEdit(row) {
            if (['ENVIADO', 'APROVADO'].includes(row.status)) {
                await this.$messageBox
                    .confirm(
                        this.$t(
                            'contrato_rastreabilidade.messages.modal_change_status'
                        ),
                        this.$t(
                            'contrato_rastreabilidade.messages.modal_change_status_title'
                        ),
                        {
                            confirmButtonText: this.$t(
                                'contrato_rastreabilidade.words.confirm'
                            ),
                            cancelButtonText: this.$t(
                                'contrato_rastreabilidade.words.cancel'
                            )
                        }
                    )
                    .then(() => {
                        this.$router.push({
                            name: 'contrato-rastreabilidade-editar',
                            params: { id: row.id }
                        });
                    })
                    .catch(() => {});
            } else {
                await this.$router.push({
                    name: 'contrato-rastreabilidade-editar',
                    params: { id: row.id }
                });
            }
        },
        async handleRemove(row) {
            await this.$messageBox
                .confirm(
                    this.$t(
                        'contrato_rastreabilidade.messages.modal_delete_report'
                    ),
                    this.$t(
                        'contrato_rastreabilidade.messages.modal_delete_report_title'
                    ),
                    {
                        confirmButtonText: this.$t(
                            'contrato_rastreabilidade.words.confirm'
                        ),
                        cancelButtonText: this.$t(
                            'contrato_rastreabilidade.words.cancel'
                        )
                    }
                )
                .then(async () => {
                    try {
                        await this.destroyTraceabilityReport(row.id);
                        const index = this.data.reports.findIndex(
                            item => item.id === row.id
                        );
                        this.data.reports.splice(index, 1);
                        this.$notify.success(
                            this.$t(
                                'contrato_rastreabilidade.messages.remove_contract_success'
                            )
                        );
                    } catch (error) {
                        this.$notify.error(
                            this.$t(
                                'contrato_rastreabilidade.words.error_message'
                            )
                        );
                    }
                })
                .catch(() => {});
        },
        async handleViewReport(row) {
            try {
                const response = await this.downloadTraceabilityReport(row.id);
                const blob = new Blob([response.data], {
                    type: 'application/octet-stream'
                });
                const fileURL = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = fileURL;
                a.download = 'report.pdf';
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(fileURL);
            } catch (error) {
                this.$notify.error(
                    this.$t('contrato_rastreabilidade.words.error_message')
                );
            }
        },
        async openSendForApproval(row) {
            if (
                row.contractNumber &&
                row.contractVolume &&
                row.culture &&
                row.receivingLocation &&
                row.client &&
                row.contractDate
            ) {
                await this.fetchTraceabilityReport(row.id);
                this.showDialogContractFinish = true;
            } else {
                await this.$router.push({
                    name: 'contrato-rastreabilidade-editar',
                    params: { id: row.id }
                });
            }
        },
        formatNumber(row, column, reportVolume, index) {
            return formatters.formatNumber(reportVolume);
        }
    }
};
</script>

<style scoped lang="scss">
.farm-list-table {
    .el-table {
        .cell {
            word-break: break-word !important;
        }

        .action-button {
            cursor: pointer;
        }
    }

    .farm-list-table__pagination {
        display: grid;
        grid-template-columns: 2fr auto;
        margin-top: 20px;
        @media (max-width: 768px) {
            grid-template-columns: 1fr;
        }

        .total {
            display: flex;
            align-items: center;
            @media (max-width: 768px) {
                flex-direction: column;
            }
        }

        .prev_next {
            display: flex;
            @media (max-width: 768px) {
                justify-content: center;
                margin-top: 20px;
            }
        }
    }
}
</style>
